import { pluck, pipe, curriedPluck } from '@/utils/helper'
import FetchEventUseCase from '@/usecases/event/FetchEventUseCase'
import FetchEventAlbumsUseCase from '@/usecases/event/FetchEventAlbumsUseCase'
import SearchEventUchinokoPhotosUseCase from '@/usecases/event/SearchEventUchinokoPhotosUseCase'
import FetchGroupsUseCase from '@/usecases/group/FetchGroupsUseCase'
import FetchSchoolsUseCase from '@/usecases/school/FetchSchoolsUseCase'
import FetchGroupSettingUseCase from '@/usecases/group/FetchGroupSettingUseCase'
import FetchChildrenUseCase from '@/usecases/child/FetchChildrenUseCase'
import FetchCartQueryStatusUseCase from '@/usecases/event/FetchCartQueryStatusUseCase'
import SearchEventCartQueryPhotosUseCase from '@/usecases/event/SearchEventCartQueryPhotosUseCase'
import GroupRepository from '@/repositories/GroupRepository'

import { UchinokoData } from '@/types/domain/uchinoko'
import ChildEntity from '@/entities/Child'
import { ICartQueryStatusProps } from '@/entities/C4'

export interface ILoadEventUseCaseParams {
  fetchEventUseCase: FetchEventUseCase
  fetchEventAlbumsUseCase: FetchEventAlbumsUseCase
  searchEventUchinokoPhotosUseCase: SearchEventUchinokoPhotosUseCase
  fetchGroupsUseCase: FetchGroupsUseCase
  fetchSchoolsUseCase: FetchSchoolsUseCase
  fetchGroupSettingUseCase: FetchGroupSettingUseCase
  fetchChildrenUseCase: FetchChildrenUseCase
  fetchCartQueryStatusUseCase: FetchCartQueryStatusUseCase
  searchEventCartQueryPhotosUseCase: SearchEventCartQueryPhotosUseCase
  groupRepository: GroupRepository
}
export default class LoadEventUseCase implements BaseUseCase {
  private fetchEventUseCase: FetchEventUseCase
  private fetchEventAlbumsUseCase: FetchEventAlbumsUseCase
  private searchEventUchinokoPhotosUseCase: SearchEventUchinokoPhotosUseCase
  private fetchGroupsUseCase: FetchGroupsUseCase
  private fetchSchoolsUseCase: FetchSchoolsUseCase
  private fetchGroupSettingUseCase: FetchGroupSettingUseCase
  private fetchChildrenUseCase: FetchChildrenUseCase
  private fetchCartQueryStatusUseCase: FetchCartQueryStatusUseCase
  private searchEventCartQueryPhotosUseCase: SearchEventCartQueryPhotosUseCase
  private groupRepository: GroupRepository

  constructor(params: ILoadEventUseCaseParams) {
    this.fetchEventUseCase = params.fetchEventUseCase
    this.fetchEventAlbumsUseCase = params.fetchEventAlbumsUseCase
    this.searchEventUchinokoPhotosUseCase = params.searchEventUchinokoPhotosUseCase
    this.fetchGroupsUseCase = params.fetchGroupsUseCase
    this.fetchSchoolsUseCase = params.fetchSchoolsUseCase
    this.fetchGroupSettingUseCase = params.fetchGroupSettingUseCase
    this.fetchChildrenUseCase = params.fetchChildrenUseCase
    this.fetchCartQueryStatusUseCase = params.fetchCartQueryStatusUseCase
    this.searchEventCartQueryPhotosUseCase = params.searchEventCartQueryPhotosUseCase
    this.groupRepository = params.groupRepository
  }

  async execute({ eventId, extra }: { eventId: number; extra?: boolean }) {
    try {
      const event = await this.fetchEventUseCase.execute(eventId)

      // FIXME: Use entity method
      // パスワードロックがかかったイベント, ステータスがcloseのイベントに付随するリソースは取得しない
      if ((!event.passwordUnlockFlag && event.passwordType === 'common') || event.publishStatus === 'close') return

      // Groupsとそれに紐づくSchoolsを取得してイベントを閲覧可能なGroupを特定
      // その後上記のGroupSettingsを取得してかお検索設定を確認
      const groups = await this.fetchGroupsUseCase.execute()
      await Promise.all(groups.map(async group => await this.fetchSchoolsUseCase.execute(group.props.id)))
      const targetGroups = this.groupRepository.getGroupsByEvent({ eventId })
      await Promise.all(targetGroups.map(async group => await this.fetchGroupSettingUseCase.execute(group.props.id)))
      const settings = this.groupRepository.getGroupSettings(pluck(pluck(targetGroups, 'props'), 'id'))
      let hasAdminPrivilege = false
      const adminGroup = this.groupRepository.getAdminGroup()
      if (adminGroup) {
        const adminSchools = this.groupRepository.getSchools(adminGroup.props.id)
        hasAdminPrivilege = adminSchools.some(school => school.props.eventIds.includes(eventId))
      }
      const isUchinokoSearchEnabled = settings.some(setting => setting.isUchinokoSearchEnabled)

      await Promise.all(targetGroups.map(async group => await this.fetchChildrenUseCase.execute(group.props.id)))
      const children = this.groupRepository.getChildrenByEvent(eventId)
      const childrenList = Object.values(children)
        .reduce((acc, ac) => [...acc, ...ac], [] as ChildEntity[])
        .filter(child => child.props.profileImage)
      const childIds = pipe(() => childrenList, curriedPluck('props'), curriedPluck('id'))
      const profileSearchEnabledChildren = settings.map(setting => {
        return setting.children
          .map(children => {
            if (children.is_profile_image_search_enabled) return children.child_id
          })
          .filter((n): n is number => {
            return typeof n === 'number'
          })
      })
      const isProfileSearchEnabled = profileSearchEnabledChildren
        .map(children => [...children, ...childIds].filter(NumbersToCompare => children.includes(NumbersToCompare) && childIds.includes(NumbersToCompare)).length > 0)
        .some(bool => bool === true)
      let uchinokoDataList: UchinokoData[] = []
      let cartQueryStatus: ICartQueryStatusProps | undefined

      // かお検索設定がONになっている場合のみ「かお」を取得
      if (isUchinokoSearchEnabled) {
        cartQueryStatus = await this.fetchCartQueryStatusUseCase.execute({ eventId })
        const isFirstFaceSearch = cartQueryStatus && cartQueryStatus.hasCartQuery && !cartQueryStatus.hasOrderHistory && hasAdminPrivilege && !isProfileSearchEnabled
        if (isFirstFaceSearch) {
          if (extra) {
            [uchinokoDataList] = await Promise.all([this.searchEventCartQueryPhotosUseCase.execute({ eventId, recommend: false }), this.fetchEventAlbumsUseCase.execute(eventId)])
          } else {
            [uchinokoDataList] = await Promise.all([this.searchEventCartQueryPhotosUseCase.execute({ eventId, recommend: true }), this.fetchEventAlbumsUseCase.execute(eventId)])
          }
        } else if (cartQueryStatus && cartQueryStatus.hasOrderHistory) {
          if (extra) {
            [uchinokoDataList] = await Promise.all([this.searchEventUchinokoPhotosUseCase.execute({ eventId, recommend: false }), this.fetchEventAlbumsUseCase.execute(eventId)])
          } else {
            [uchinokoDataList] = await Promise.all([this.searchEventUchinokoPhotosUseCase.execute({ eventId, recommend: true }), this.fetchEventAlbumsUseCase.execute(eventId)])
          }
        } else if (cartQueryStatus && !cartQueryStatus.hasOrderHistory && isProfileSearchEnabled) {
          if (extra) {
            await Promise.all([this.searchEventUchinokoPhotosUseCase.execute({ eventId, ignoreOrderHistory: true, recommend: false }), this.fetchEventAlbumsUseCase.execute(eventId)])
          } else {
            await Promise.all([this.searchEventUchinokoPhotosUseCase.execute({ eventId, ignoreOrderHistory: true, recommend: true }), this.fetchEventAlbumsUseCase.execute(eventId)])
          }
        } else {
          await this.fetchEventAlbumsUseCase.execute(eventId)
        }
        if (uchinokoDataList.length) {
          const pageInPhotoIds: number[] = []
          uchinokoDataList.map(item => {
            item.photos.map(photosItem => {
              pageInPhotoIds.push(photosItem.id)
            })
          })
          return {
            pageInPhotoIds,
            isFirstFaceSearch
          }
        }
      } else {
        await this.fetchEventAlbumsUseCase.execute(eventId)
      }
    } catch (error) {
      throw error
    }
  }
}
